import React, { useState, useCallback, useEffect } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { Empleado, EmpleadosPagingRes } from '@/shared/interfaces/erp/empleados';
import { getUrlParamsFetch, TipoEmpleadoEnumChoice, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios';

const { get } = erpApi();


interface CustomAutoCompleteVendedorProps {
  onChangeValue: (value: Empleado) => void
}
const CustomAutoCompleteVendedor: React.FC<CustomAutoCompleteVendedorProps> = ({
  onChangeValue
}) => {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<Empleado[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Función para obtener los datos de la API según el valor de búsqueda y la página.
  const fetchOptions = async (searchValue: string, page: number): Promise<{ options: Empleado[]; hasMore: boolean }> => {
    try {

      const stateParams = {
        estado: true,
        tipo_empleado: TipoEmpleadoEnumChoice.VENDEDOR,
        razon_social: searchValue,
        page: page
      }

      const queryParams = getUrlParamsFetch(stateParams ?? {});

      const response = await get<EmpleadosPagingRes>(`${urlBackend}api/v1/empleado/?${queryParams}`, true);

      return {
        options: response.data.data,
        hasMore: true, // true si hay más datos
      };
    } catch (error) {
      console.error('Error al obtener los datos:', error);
      return { options: [], hasMore: false };
    }
  };

  useEffect(() => {
    initialData()

    return () => {
    }

  }, [])

  async function initialData() {
    setLoading(true);
    const { options: newOptions, hasMore: more } = await fetchOptions(search, 1);
    setOptions(newOptions);
    setHasMore(more);
    setLoading(false);
  }

  // Efecto para aplicar debounce a la búsqueda.
  // Cada vez que cambia "search", se espera 500 ms antes de consultar la API.
  // Se reinician las opciones y la página actual.
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (search.trim() === '') {
        initialData()
        return;
      }
      setLoading(true);
      const { options: newOptions, hasMore: more } = await fetchOptions(search, 1);
      setOptions(newOptions);
      setPage(1);
      setHasMore(more);
      setLoading(false);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  // Función para cargar más opciones (paginación) cuando se llega al final de la lista.
  const loadMoreOptions = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    const nextPage = page + 1;
    const { options: newOptions, hasMore: more } = await fetchOptions(search, nextPage);
    setOptions(prev => [...prev, ...newOptions]);
    setPage(nextPage);
    setHasMore(more);
    setLoading(false);
  }, [loading, hasMore, page, search]);

  // Manejador para actualizar el valor del input y disparar la búsqueda.
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string) => {
      setSearch(newInputValue);
    },
    []
  );

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, value: any) => {
      onChangeValue(value ?? '')
    },
    []
  );

  return (
    <Autocomplete
      freeSolo
      noOptionsText={'No hay opciones'}
      options={options}
      loading={loading}
      getOptionLabel={(option: Empleado | string) =>
        typeof option === 'string' ? option : option.razon_social || ''
      }
      onInputChange={handleInputChange}
      onChange={handleChange}
      renderInput={(params) => (
        <TextField {...params} label="Asesor" variant="outlined" autoComplete="off" />
      )}
      ListboxProps={{
        onScroll: (event: React.UIEvent<HTMLElement>) => {
          const listboxNode = event.currentTarget;
          // Cuando se llega casi al final del scroll, carga más datos
          if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1) {
            loadMoreOptions();
          }
        },
      }}
    />
  );
};

export default CustomAutoCompleteVendedor;
